import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Palette, Monitor, Sun, Moon, Check } from 'lucide-react';

const AppearanceSettings: React.FC = () => {
  const [selectedTheme, setSelectedTheme] = useState('light');
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [selectedChart, setSelectedChart] = useState('default');

  const themes = [
    { id: 'light', name: 'Light', icon: Sun, preview: 'bg-white border-gray-200' },
    { id: 'dark', name: 'Dark', icon: Moon, preview: 'bg-gray-900 border-gray-700' },
    { id: 'auto', name: 'System', icon: Monitor, preview: 'bg-gradient-to-r from-white to-gray-900' },
  ];

  const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  const chartStyles = [
    {
      id: 'default',
      name: 'Default',
      description: 'Standard company branding',
      preview: 'bg-gradient-to-r from-blue-100 to-purple-100',
    },
    {
      id: 'minimal',
      name: 'Minimal',
      description: 'Clean and modern',
      preview: 'bg-gradient-to-r from-gray-100 to-gray-200',
    },
    {
      id: 'vibrant',
      name: 'Vibrant',
      description: 'Bold and colorful',
      preview: 'bg-gradient-to-r from-pink-100 to-orange-100',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 p-3">
          <Palette className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appearance</h1>
          <p className="text-gray-600">Customize how your dashboard looks and feels</p>
        </div>
      </div>

      {/* Theme Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Theme</h3>
        <p className="text-sm text-gray-600">Choose your preferred theme</p>

        <div className="grid grid-cols-3 gap-4">
          {themes.map((theme) => {
            const Icon = theme.icon;
            return (
              <motion.button
                key={theme.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedTheme(theme.id)}
                className={`relative rounded-2xl border-2 p-4 transition-all ${
                  selectedTheme === theme.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className={`mb-3 h-12 w-full rounded-lg ${theme.preview}`}></div>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-900">{theme.name}</span>
                </div>
                {selectedTheme === theme.id && (
                  <div className="absolute -top-2 -right-2 rounded-full bg-blue-500 p-1">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Brand Color */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Brand Color</h3>
        <p className="text-sm text-gray-600">Select or customize your brand color</p>

        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {colors.map((color) => (
              <motion.button
                key={color}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedColor(color)}
                className={`relative h-10 w-10 rounded-full border-2 ${
                  selectedColor === color ? 'border-gray-400' : 'border-gray-200'
                }`}
                style={{ backgroundColor: color }}
              >
                {selectedColor === color && <Check className="absolute inset-0 m-auto h-4 w-4 text-white" />}
              </motion.button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
            <span className="text-sm text-gray-600">#</span>
            <input
              type="text"
              value={selectedColor.replace('#', '')}
              onChange={(e) => setSelectedColor(`#${e.target.value}`)}
              className="w-20 font-mono text-sm text-gray-900 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Chart Style */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Dashboard Charts</h3>
        <p className="text-sm text-gray-600">How charts are displayed in your dashboard</p>

        <div className="grid grid-cols-3 gap-4">
          {chartStyles.map((style) => (
            <motion.button
              key={style.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedChart(style.id)}
              className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                selectedChart === style.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              <div className={`mb-3 h-16 w-full rounded-lg ${style.preview}`}>
                <div className="flex h-full items-end justify-between p-2">
                  <div className="h-8 w-2 rounded bg-white/50"></div>
                  <div className="h-6 w-2 rounded bg-white/50"></div>
                  <div className="h-10 w-2 rounded bg-white/50"></div>
                  <div className="h-4 w-2 rounded bg-white/50"></div>
                </div>
              </div>
              <h4 className="font-semibold text-gray-900">{style.name}</h4>
              <p className="text-sm text-gray-600">{style.description}</p>
              {selectedChart === style.id && (
                <div className="absolute -top-2 -right-2 rounded-full bg-blue-500 p-1">
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6">
        <button className="rounded-xl bg-gray-200 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-300">
          Cancel
        </button>
        <button className="rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-all hover:bg-blue-700">
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default AppearanceSettings;
